import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Autocomplete, Button, FormControl, Grid, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";
import PageLayout from "components/layout/PageLayout";
import Loader from "components/lists/Loader";
import AdditionalItemsList from "components/Operations/AdditionalItemsList";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { AdditionalItem, AssociatedClient, AssociatedMovementOption, Client, Movement } from "types";
import { MOVEMENTS_URL } from "constants/urls";
import OperationPrintDetails from "components/Prints/Operations/OperationPrintDetails";
import OperationPrintHeaderDetails from "components/Prints/Operations/OperationPrintHeaderDetails";
import { PrintLayout } from "components/Prints/PrintLayout";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import { dateISOFormat } from "helpers";
import { DATE_VIEWS, defaultMovement, MovementTypes, STATUS_CODES } from "../constants";
import CustomNumberInput from "components/CustomNumberInput";
import { initMovementDate } from "helpers/movement";
import { queryBuilder } from "helpers/query";

export default function AdditionalsPage() {
  const printRef = useRef(null);
  const navigate = useNavigate();
  const { showSnackBar } = useToast();

  const [movement, setMovement] = useState<Movement>({
    ...defaultMovement,
    movement_type_id: MovementTypes.Additional,
  });
  const [movementToPrint, setMovementToPrint] = useState<Movement | undefined>();
  const [movementLoading, setMovementLoading] = useState<boolean>(false);

  const { createData: createMovement, firstLoad: onMovementFirstLoad, fetchData: getMovement } = useCrud(MOVEMENTS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: fetchAssociatedMovements } = useCrud<Movement>(`${MOVEMENTS_URL}all`, {
    disableAutoFetch: true,
  });

  const { userData, stores, clients, associatedClients, additionalItems, users } = useSession();

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientAssociatedClients, setClientAssociatedClients] = useState<AssociatedClient[]>([]);
  const [clientAdditionalItems, setClientAdditionalItems] = useState<AdditionalItem[]>([]);
  const [additionalItemRowIndex, setAdditionalItemRowIndex] = useState<number>(0);
  const [selectedParentClient, setSelectedParentClient] = useState<Client | null>(null);
  const [hasAssociatedMovement, setHasAssociatedMovement] = useState(false);
  const [associatedMovementOptions, setAssociatedMovementOptions] = useState<AssociatedMovementOption[]>([]);

  const associatedMovementTypeLabel = (movementTypeId: number) => {
    switch (movementTypeId) {
      case MovementTypes.Income:
        return "Ingreso";
      case MovementTypes.Discharge:
        return "Egreso";
      case MovementTypes.Preparation:
        return "Pedido";
      default:
        return "Movimiento";
    }
  };

  const associatedMovementOptionLabel = (movement: Movement) => {
    const type = associatedMovementTypeLabel(movement.movement_type_id);
    const date = new Date(movement.date).toLocaleDateString();

    return `${type} #${movement.id} | Fecha: ${date} | ${movement.numero_remito ? `Remito: ${movement.numero_remito}` : "Sin remito"} | Doc. cliente: ${movement.client_document || "sin documento"}`;
  };

  const handleChange = useCallback((name: string, value: any) => {
    setMovement((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setMovementLoading(true);
      try {
        const response = await createMovement([{ ...movement, store_id: null }], true);
        if (response.status === STATUS_CODES.OK) {
          const aggregatedMovement = await getMovement(`${response.data[0].id}`);
          setMovementToPrint(aggregatedMovement.data[0]);
        } else {
          showSnackBar(
            response.status === STATUS_CODES.BAD_REQUEST
              ? "El número de remito ya se encuentra registrado, por favor modifiquelo e intente nuevamente."
              : "Hubo un error con la solicitud, por favor intente nuevamente",
            "error"
          );
          setMovementLoading(false);
        }
      } catch (error) {
        showSnackBar("Hubo un error inesperado, por favor intente nuevamente", "error");
        setMovementLoading(false);
      }
    },
    [createMovement, getMovement, movement, showSnackBar]
  );

  useEffect(() => {
    if (userData) {
      handleChange("created_by", userData.id);
    }
  }, [userData, handleChange]);

  useEffect(() => {
    const associatedClientOwnerId = selectedParentClient?.id ?? movement.client_id;

    setClientAssociatedClients(
      associatedClients.filter((ac) => ac.client?.id === associatedClientOwnerId)
    );
    setClientAdditionalItems(
      additionalItems.filter((ai) => ai.client?.id === movement.client_id || !ai.client_id)
    );
  }, [movement.client_id, selectedParentClient, associatedClients, additionalItems]);

  useEffect(() => {
    if (movementToPrint) {
      handlePrint();
    }
  }, [movementToPrint]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      navigate("/operations");
      showSnackBar("Se ha creado el adicional correctamente", "success");
      setMovementLoading(false);
    },
  });

  const userOptions = useMemo(
    () => users.map((u) => ({ id: u.id, label: `${u.first_name} ${u.last_name}` })),
    [users]
  );

  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) => c.parent_client_id == null)
        .map((c) => ({
          id: c.id,
          label: `${c.code} - ${c.razon_social}`,
        })),
    [clients]
  );

  const lineOptions = useMemo(() => {
    if (!selectedParentClient) return [];

    return clients
      .filter(
        (c) => c.parent_client_id === selectedParentClient.id && c.is_active
      )
      .map((c) => ({
        id: c.id,
        label: `${c.code} - ${c.razon_social}`,
      }));
  }, [clients, selectedParentClient]);

  const selectedLineOption =
    lineOptions.find((line) => line.id === movement.client_id) ?? null;

  const hasSelectedParentClient = Boolean(selectedParentClient);
  const parentHasLines = lineOptions.length > 0;
  const requiresLine = hasSelectedParentClient && parentHasLines;

  const canSubmit = Boolean(
    movement.client_id &&
      (!requiresLine || selectedLineOption) &&
      (!hasAssociatedMovement || movement.movement_id)
  );

  useEffect(() => {
    initMovementDate(movement.date, handleChange, { setSettlement: true });
  }, []);

  useEffect(() => {
    const loadAssociatedMovements = async () => {
      if (!selectedLineOption || !selectedParentClient) {
        setAssociatedMovementOptions([]);
        handleChange("movement_id", null);
        return;
      }

      const query = queryBuilder({
        clientId: selectedParentClient.id,
        movementTypeIds: [
          MovementTypes.Income,
          MovementTypes.Discharge,
          MovementTypes.Preparation,
        ].join(","),
        includeDrafts: false,
        includeAdjustments: false,
      });

      const response = await fetchAssociatedMovements(query, true);

      const movements = response.status === STATUS_CODES.OK
        ? response.data ?? []
        : [];

      const lineMovementOptions = movements.filter((associatedMovement: Movement) => {
        if (associatedMovement.client_id === movement.client_id) return true;

        return associatedMovement.articles?.some(
          (article) => article.client_id === movement.client_id
        );
      });

      setAssociatedMovementOptions(
        lineMovementOptions.map((associatedMovement: Movement) => ({
          id: Number(associatedMovement.id),
          label: associatedMovementOptionLabel(associatedMovement),
          movement: associatedMovement,
        }))
      );
    };

    loadAssociatedMovements();
  }, [movement.client_id, selectedLineOption, selectedParentClient]);

  return (
    <PageLayout pageTitle="Adicionales" pageDesc="Orden de adicionales por cliente">
      {movementToPrint && (
        <PrintLayout
          ref={printRef}
          headerInfo={
            <OperationPrintHeaderDetails
              movement={movementToPrint}
              stores={stores}
            />
          }
        >
          <OperationPrintDetails
            users={users}
            movement={movementToPrint}
            client={movementToPrint.client || selectedClient}
          />
        </PrintLayout>
      )}
      <form onSubmit={handleSubmit}>
        {movementLoading ? (
          <Loader isLoading={movementLoading} isFirstLoad={onMovementFirstLoad} />
        ) : (
          <Grid container columnSpacing={1}>
            <Grid item xs={12} sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
              <Grid item xs={4} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Tooltip
                  title="Al activarlo, este adicional queda relacionado con un ingreso, egreso o pedido existente de la línea seleccionada."
                  arrow
                >
                  <Typography variant="body2" fontWeight="bold">
                    Asociar adicional a movimiento existente
                  </Typography>
                </Tooltip>

                <Switch
                  checked={hasAssociatedMovement}
                  onChange={(e) => {
                    setHasAssociatedMovement(e.target.checked);

                    if (!e.target.checked) {
                      handleChange("movement_id", null);
                    }
                  }}
                />
              </Grid>

              {hasAssociatedMovement && (
                <Grid item xs={4}>
                  <FormControl fullWidth margin="none">
                    <Autocomplete
                      id="associated-movement"
                      autoComplete
                      disabled={!selectedLineOption}
                      value={
                        associatedMovementOptions.find(
                          (option) => option.id === movement.movement_id
                        ) ?? null
                      }
                      options={associatedMovementOptions}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(e, value) => {
                        handleChange("movement_id", value?.id ?? null);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Movimiento asociado" />
                      )}
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="user"
                  autoComplete
                  disabled
                  value={userOptions.find((u) => u.id === movement.created_by) || null}
                  options={userOptions}
                  onChange={(e, value) => handleChange("created_by", value?.id)}
                  renderInput={(params) => <TextField {...params} required label="Usuario" />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="author"
                  autoComplete
                  value={userOptions.find((u) => u.id === movement.updated_by) || null}
                  options={userOptions}
                  onChange={(e, value) => handleChange("updated_by", value?.id)}
                  renderInput={(params) => <TextField {...params} required label="Preparó" />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <DateTimePicker
                  disableFuture
                  label="Fecha"
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                  slots={{
                    actionBar: DateTimePickerActionBar,
                  }}
                  views={DATE_VIEWS}
                  value={movement.date ? new Date(movement.date) : null}
                  onChange={(value) => {
                    const formattedDate = value ? dateISOFormat(value.toISOString()) : null;
                    handleChange("date", formattedDate);
                    handleChange("settlement_date", formattedDate);
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="client"
                  autoComplete
                  value={
                    selectedParentClient
                      ? {
                          id: selectedParentClient.id,
                          label: `${selectedParentClient.code} - ${selectedParentClient.razon_social}`,
                        }
                      : null
                  }
                  options={clientOptions}
                  onChange={(e, value) => {
                    const parent = clients.find((c) => c.id === value?.id) || null;
                    const parentLines = clients.filter((c) => c.parent_client_id === value?.id && c.is_active);

                    setSelectedParentClient(parent);
                    setSelectedClient(parentLines.length > 0 ? null : parent);
                    handleChange("client_id", parentLines.length > 0 ? 0 : value?.id ?? 0);
                    handleChange("additional_items", []);
                    handleChange("associated_client", "");
                    handleChange("movement_id", null);
                  }}
                  renderInput={(params) => <TextField {...params} required label="Cliente" />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="client-line"
                  autoComplete
                  disabled={!hasSelectedParentClient || !parentHasLines}
                  value={selectedLineOption}
                  options={lineOptions}
                  onChange={(e, value) => {
                    const line = clients.find((c) => c.id === value?.id) || null;
                    setSelectedClient(line);
                    handleChange("client_id", value?.id ?? 0);
                    handleChange("additional_items", []);
                    handleChange("associated_client", "");
                    handleChange("movement_id", null);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} required={requiresLine} label="Línea" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="associated-client"
                  options={clientAssociatedClients.map((ac) => ({ label: ac.razon_social }))}
                  value={{ label: movement.associated_client }}
                  onChange={(e, value) => handleChange("associated_client", value?.label)}
                  renderInput={(params) => <TextField {...params} required label="Cliente Asociado" />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth required margin="none">
                <TextField
                  data-cy="income-client-doc"
                  label="Documento del Cliente"
                  value={movement.client_document}
                  onChange={(e) => handleChange("client_document", e.target.value)}
                  fullWidth
                  required
                />
              </FormControl>  
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <CustomNumberInput
                  label="Pallets"
                  value={movement.pallets}
                  onChange={(e) => handleChange("pallets", Number(e.target.value))}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <CustomNumberInput
                  label="Peso"
                  value={movement.weight}
                  onChange={(e) => handleChange("weight", Number(e.target.value))}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="none">
                <CustomNumberInput
                  label="Número de Remito"
                  value={movement.numero_remito}
                  onChange={(e) => handleChange("numero_remito", e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="observations"
                label="Observaciones"
                type="text"
                value={movement.observation}
                onChange={(e) => handleChange("observation", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} my={2}>
              <AdditionalItemsList
                additionalItems={clientAdditionalItems}
                movement={movement}
                onMovementUpdate={handleChange}
                rowIndex={additionalItemRowIndex}
                setRowIndex={setAdditionalItemRowIndex}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={!canSubmit}>
                Enviar
              </Button>
            </Grid>
          </Grid>
        )}
      </form>
    </PageLayout>
  );
}
